import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import Avatar from '@divvy-web/skylab.avatar'
import BasicButton, {
  BASIC_BUTTON_COLOR_DANGER,
  BASIC_BUTTON_COLOR_NEUTRAL,
  BASIC_BUTTON_SIZE_MEDIUM,
  BASIC_BUTTON_TYPE_FLAT,
} from '@divvy-web/skylab.basicbutton'
import Icon, { ICON_SIZE_REGULAR } from '@divvy-web/skylab.icon'
import Tag from '@divvy-web/skylab.tag'

import { AddPersonDrawerOpenable } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

export interface PersonRecordWithActionsProps {
  firstName: string
  lastName: string
  personId: string
  subtext?: {
    showTag: boolean
    infoComplete?: boolean
  }
  onDeleteClick: (personId: string) => Promise<void>
  disableEdit: boolean
  disableDelete: boolean
  editDrawer: React.ComponentType<AddPersonDrawerOpenable & { personId: string }>
  trackerId: 'authorizedSigner' | 'companyOwner'
}

const PersonRecordWithActions = ({
  firstName,
  lastName,
  personId,
  subtext = { showTag: false, infoComplete: true },
  onDeleteClick,
  editDrawer: EditDrawer,
  disableEdit,
  disableDelete,
  trackerId,
}: PersonRecordWithActionsProps) => {
  const [getClassName, makeTestId] = useNameFormatter('PersonRecordWithActions')
  const { formatMessage } = useIntl()
  const { tracker } = useWebTracking()
  const [searchParams] = useSearchParams()

  // logic for automatically opening the edit drawer for authorized signer for when they click edit from review page
  const isEditPerson =
    !!searchParams.get('personId') && searchParams.get('personId') === personId && trackerId === 'authorizedSigner'

  const [isShowingDrawer, setIsShowingDrawer] = useState(isEditPerson)

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      <div
        key={personId}
        className={styles[getClassName('list-item')]}
      >
        <div>
          {subtext?.infoComplete && <Avatar fallbackText={`${firstName?.charAt(0)} ${lastName?.charAt(0)}`}></Avatar>}
          {!subtext?.infoComplete && (
            <Icon
              name='feedbackWarning'
              size={ICON_SIZE_REGULAR}
            />
          )}
        </div>
        <div>
          <span className='fs-mask'>
            {firstName} {lastName}
          </span>
          {subtext && (
            <div className={styles[getClassName('subtext')]}>
              {!subtext.infoComplete && (
                <div
                  className={styles[getClassName('incomplete-info')]}
                  data-testid={makeTestId('information-incomplete')}
                >
                  <FormattedMessage
                    defaultMessage='Information incomplete'
                    id='sputnik.PersonRecordWithActions__required'
                  />
                </div>
              )}
              {!subtext.infoComplete && subtext.showTag && <div className={styles[getClassName('label-divider')]} />}
              {subtext.showTag && (
                <Tag
                  dataTestId={makeTestId('authorized-signer')}
                  type='primary'
                >
                  <FormattedMessage
                    defaultMessage='Authorized signer'
                    id='sputnik.PersonRecordWithActions__complete'
                  />
                </Tag>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles[getClassName('actions')]}>
        <BasicButton
          color={BASIC_BUTTON_COLOR_NEUTRAL}
          disabled={disableEdit}
          icon='edit'
          name={formatMessage(
            {
              defaultMessage: 'Edit person {firstName} {lastName}',
              id: 'sputnik.PersonRecordWithActions__FYsC+I',
            },
            { firstName, lastName },
          )}
          size={BASIC_BUTTON_SIZE_MEDIUM}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={() => {
            tracker?.track('se_signup__edit_person__fired', {
              person_type: trackerId,
            })
            setIsShowingDrawer(true)
          }}
        />
        <BasicButton
          color={BASIC_BUTTON_COLOR_DANGER}
          disabled={disableDelete}
          icon='trash'
          name={formatMessage(
            {
              defaultMessage: 'Delete person {firstName} {lastName}',
              id: 'sputnik.PersonRecordWithActions__UlUg7e',
            },
            { firstName, lastName },
          )}
          size={BASIC_BUTTON_SIZE_MEDIUM}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={() => {
            tracker?.track('se_signup__delete_person__fired', {
              person_type: trackerId,
            })
            onDeleteClick(personId)
          }}
        />
      </div>
      <EditDrawer
        isShowing={isShowingDrawer}
        personId={personId}
        onClose={() => setIsShowingDrawer(false)}
      />
    </div>
  )
}

export default PersonRecordWithActions
